import {Module} from '@mamdev/cad.static-next-lib';
import {Accessibility} from '../../js/services/accessibility';
import Viewport from '../../js/services/viewport';

export default class Navigation extends Module {
  private navMoreLayer!: HTMLElement;

  /** Init. from DOM */
  static override attachTo(root: HTMLElement): Navigation {
    return new Navigation(root);
  }

  constructor(root: HTMLElement) {
    super(root);
    this.name_ = 'navigation';
  }

  /** Start module */
  override start(): void {
    try {
      this.enableNavigation();
      this.enableKeyboardNavigation();
    } catch (e) {
      this.logUnknownError(e);
    }
  }

  /** Enable the navigation functionality */
  private enableNavigation(): void {
    this.navMoreLayer = this.root_.getElementsByClassName('more-nav-layer')[0] as HTMLElement;

    if (!this.navMoreLayer) {
      return;
    }

    this.navMoreLayer.querySelectorAll('.has-subnav').forEach(el => {
      el.addEventListener('click', ev => {
        if (Viewport.isDesktop()) {
          ev.stopPropagation();

          if (!(ev.target as Element).parentElement?.classList.contains('subnav-item')) {
            ev.preventDefault(); // only links in submenus will be followed
          }

          if (el.classList.contains('open')) {
            this.closeOpenMenu();
          } else {
            this.openMenu(el);
          }
        }
      });
    });

    document.addEventListener('click', () => this.closeOpenMenu());
  }

  private closeOpenMenu(): void {
    try {
      const openMenuElement = this.navMoreLayer.getElementsByClassName('has-subnav open')[0] as HTMLElement | undefined;

      if (openMenuElement) {
        openMenuElement.classList.remove('open');
        openMenuElement.firstElementChild?.setAttribute('aria-expanded', 'false');
      }
    } catch (e) {
      this.logUnknownError(e);
    }
  }

  private openMenu(el: Element): void {
    try {
      this.closeOpenMenu();
      el.classList.add('open');
      el.firstElementChild?.setAttribute('aria-expanded', 'true');
    } catch (e) {
      this.logUnknownError(e);
    }
  }

  /** Focuses the first child element of the given element */
  private focusFirstChild(el: Element | null | undefined): void {
    (el?.firstElementChild as HTMLElement | undefined)?.focus();
  }

  /**
   * Enable navigation using arrow keys for a menu element
   * For logic used and more info, see https://www.w3.org/TR/wai-aria-practices/#menu
   *
   * Rules:
   * - Left arrow: move to the previous menu bar item; if in a submenu, close the submenu as well
   * - Right arrow: move to the next menu bar item; if in a submenu, close the submenu as well
   * - Up arrow: move to the previous submenu item
   * - Down arrow: open a submenu, or move to the next submenu item
   * - Enter: open the submenu, if present
   * - Escape: when in a submenu, close the submenu and moves focus to the menu item containing the submenu
   */
  private enableKeyboardNavigation(): void {
    try {
      const menuElement = this.root_.getElementsByClassName('nav-list')[0] as HTMLElement;

      menuElement.addEventListener('keydown', (e: KeyboardEvent) => {
        const closestLi = document.activeElement?.parentElement;

        if (!closestLi) {
          return;
        }

        if (e.key === 'ArrowLeft') {
          const submenuOpen = menuElement.getElementsByClassName('has-subnav open')[0];

          // If in a submenu, close the submenu and focus on the previous menu bar item
          if (closestLi.parentElement !== menuElement) {
            e.preventDefault();
            Accessibility.showFocus();
            this.closeOpenMenu();

            const closestNavItem = closestLi.closest('.nav-item');
            const elementToFocus = closestNavItem?.previousElementSibling ?? closestNavItem;

            this.focusFirstChild(elementToFocus);
          } else if (closestLi.previousElementSibling) {
            // If in the menu bar, focus on the previous menu bar item
            e.preventDefault();
            Accessibility.showFocus();
            this.focusFirstChild(closestLi.previousElementSibling);
          } else {
            // If focus is on the first menu bar item, wrap focus to the last menu bar item
            e.preventDefault();
            Accessibility.showFocus();
            this.focusFirstChild(closestLi.parentElement?.lastElementChild);
          }

          // If a submenu was open and the new focused menu item has a submenu, open it
          if (submenuOpen && document.activeElement?.parentElement?.classList.contains('has-subnav')) {
            this.openMenu(document.activeElement.parentElement);
          } else {
            this.closeOpenMenu();
          }
        } else if (e.key === 'ArrowRight') {
          const submenuOpen = menuElement.getElementsByClassName('has-subnav open')[0];

          // If in a submenu, close the submenu and focus on the next menu bar item
          if (closestLi.parentElement !== menuElement) {
            e.preventDefault();
            Accessibility.showFocus();
            this.closeOpenMenu();

            const closestNavItem = closestLi.closest('.nav-item');
            const elementToFocus = closestNavItem?.nextElementSibling ?? closestNavItem;

            this.focusFirstChild(elementToFocus);
          } else if (closestLi.nextElementSibling) {
            // If in the menu bar, focus on the next menu bar item
            e.preventDefault();
            Accessibility.showFocus();
            this.focusFirstChild(closestLi.nextElementSibling);
          } else {
            // If focus is on the last menu bar item, wrap focus to the first menu bar item
            e.preventDefault();
            Accessibility.showFocus();
            this.focusFirstChild(closestLi.parentElement?.firstElementChild);
          }

          // If a submenu was open and the new focused menu item has a submenu, open it
          if (submenuOpen && document.activeElement?.parentElement?.classList.contains('has-subnav')) {
            this.openMenu(document.activeElement.parentElement);
          } else {
            this.closeOpenMenu();
          }
        } else if (e.key === 'ArrowDown') {
          // If in a submenu, focus on the next submenu item
          if (closestLi.parentElement !== menuElement) {
            e.preventDefault();
            Accessibility.showFocus();

            if (closestLi.nextElementSibling) {
              this.focusFirstChild(closestLi.nextElementSibling);
            } else {
              // If focus is on the last submenu item, wrap focus to the first submenu item
              this.focusFirstChild(closestLi.parentElement?.firstElementChild);
            }
          } else if (closestLi.classList.contains('has-subnav')) {
            // If in the menu bar and the current focused menu bar item has a submenu, open it
            e.preventDefault();
            Accessibility.showFocus();
            this.openMenu(closestLi);

            const firstSubmenuElement = closestLi.getElementsByClassName('subnav-item')[0]?.firstElementChild as HTMLElement | undefined;
            firstSubmenuElement?.focus();
          }
        } else if (e.key === 'ArrowUp') {
          // If in a submenu, move the focus to the previous submenu item
          if (closestLi.parentElement !== menuElement) {
            e.preventDefault();
            Accessibility.showFocus();

            if (closestLi.previousElementSibling) {
              this.focusFirstChild(closestLi.previousElementSibling);
            } else {
              // If focus is on the first submenu item, wrap focus to the last submenu item
              this.focusFirstChild(closestLi.parentElement?.lastElementChild);
            }
          }
        } else if (e.key === 'Escape') {
          this.closeOpenMenu();

          // If we are in a submenu, move the focus to the menu item which contains the submenu
          if (closestLi.parentElement !== menuElement) {
            (closestLi.closest('.nav-item')?.firstElementChild as HTMLElement | undefined)?.focus();
          }
        } else if (e.key === 'Home') {
          // Move the focus on the first item of the menu bar or submenu
          e.preventDefault();
          Accessibility.showFocus();
          this.focusFirstChild(closestLi.parentElement?.firstElementChild);
        } else if (e.key === 'End') {
          // Move the focus on the last item of the menu bar or submenu
          e.preventDefault();
          Accessibility.showFocus();
          this.focusFirstChild(closestLi.parentElement?.lastElementChild);
        }
      });
    } catch (e) {
      this.logUnknownError(e);
    }
  }
}
